<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-grid">
        <!-- About Section -->
        <div class="footer-column">
          <h3>InvestBuddy.ai</h3>
          <p class="description">
            Your intelligent investment partner. Portfolio management powered by AI.
          </p>
          <div class="social-links">
            <a href="#" aria-label="Twitter" target="_blank" rel="noopener">
              <svg class="social-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
              </svg>
            </a>
            <a href="#" aria-label="LinkedIn" target="_blank" rel="noopener">
              <svg class="social-icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Product Links -->
        <div class="footer-column">
          <h4>Product</h4>
          <ul>
            <li><router-link to="/portfolio">Portfolio</router-link></li>
            <li><router-link to="/recommendations">AI Insights</router-link></li>
            <li><router-link to="/analytics">Analytics</router-link></li>
            <li><router-link to="/alerts">Price Alerts</router-link></li>
            <li><router-link to="/pricing">Pricing</router-link></li>
          </ul>
        </div>

        <!-- Support Links -->
        <div class="footer-column">
          <h4>Support</h4>
          <ul>
            <li><router-link to="/contact">Contact Us</router-link></li>
            <li><a href="/help">Help Center</a></li>
          </ul>
        </div>

        <!-- Developers Links -->
        <div class="footer-column">
          <h4>Developers</h4>
          <ul>
            <li><router-link to="/mcp">MCP for ChatGPT</router-link></li>
            <li><router-link to="/prompts">Prompt Library</router-link></li>
            <li><router-link to="/settings/api-keys">API Keys</router-link></li>
            <li><router-link to="/terms/api">API Terms</router-link></li>
          </ul>
        </div>

        <!-- Legal Links -->
        <div class="footer-column">
          <h4>Legal</h4>
          <ul>
            <li><router-link to="/terms-of-service">Terms of Service</router-link></li>
            <li><router-link to="/privacy-policy">Privacy Policy</router-link></li>
            <li><router-link to="/cookie-policy">Cookie Policy</router-link></li>
            <li><button @click="showCookieSettings" class="cookie-btn">Cookie Settings</button></li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom">
        <p class="copyright">
          © {{ currentYear }} InvestBuddy.ai. All rights reserved.
        </p>
        <p class="data-attribution">
          Market data provided by 
          <a 
            href="https://twelvedata.com" 
            target="_blank" 
            rel="noopener noreferrer"
            class="data-link"
          >
            Twelve Data
          </a>
        </p>
        <p class="disclaimer">
          <strong>Disclaimer:</strong> InvestBuddy.ai is for informational purposes only and does not provide investment advice. 
          All investments carry risk. Please consult a financial advisor before making investment decisions.
        </p>
      </div>
    </div>
  </footer>
</template>
<script setup lang="ts">
import { computed } from 'vue'

const currentYear = computed(() => new Date().getFullYear())

const showCookieSettings = () => {
  window.dispatchEvent(new CustomEvent('show-cookie-settings'))
}
</script>

<style scoped>
.footer {
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  margin-top: 2rem;
  padding: 3rem 0 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
}

.footer-column h3 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.footer-column h4 {
  color: #111827;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.description {
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 300px;
}

.social-links {
  display: flex;
  gap: 1rem;
}

.social-links a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #f3f4f6;
  color: #4b5563;
  transition: all 0.3s ease;
}

.social-links a:hover {
  background: #2563eb;
  color: white;
  transform: translateY(-2px);
}

.social-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-column li {
  margin-bottom: 0.75rem;
}

.footer-column a,
.cookie-btn {
  color: #4b5563;
  text-decoration: none;
  transition: color 0.3s ease;
  font-size: 0.9rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
}

.footer-column a:hover,
.cookie-btn:hover {
  color: #2563eb;
}

.footer-bottom {
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
  text-align: center;
}

.copyright {
  color: #4b5563;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.data-attribution {
  color: #4b5563;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.data-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
}

.data-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.disclaimer {
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
  background: #f3f4f6;
  border-radius: 8px;
}

.disclaimer strong {
  color: #111827;
  font-weight: 600;
}

@media (max-width: 968px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .footer-column:first-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .footer {
    padding: 2rem 0 1.5rem;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .footer-column:first-child {
    grid-column: 1;
  }

  .description {
    max-width: 100%;
  }
}

.footer-column a,
.cookie-btn {
  color: var(--text-secondary);
  text-decoration: none;
  color: #1d4ed8;
  font-size: 0.9rem;
  background: none;
  border: none;
  padding: 0;
  color: #4b5563;
  text-align: left;
}

.footer-column a:hover,
.cookie-btn:hover {
  background: #f3f4f6;
}

.footer-bottom {
  padding-top: 2rem;
  color: #111827;
  text-align: center;
}

.copyright {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.data-attribution {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.data-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s ease;
}

.data-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.disclaimer {
  color: #4b5563;
  font-size: 0.75rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  padding: 1rem;
  background: #f3f4f6;
  border-radius: 8px;
}

.disclaimer strong {
  color: #111827;
  font-weight: 600;
}

@media (max-width: 968px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .footer-column:first-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 640px) {
  .footer {
    padding: 2rem 0 1.5rem;
  }

  .footer-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .footer-column:first-child {
    grid-column: 1;
  }

  .description {
    max-width: 100%;
  }
}
</style>

